import React, { useMemo, useCallback, type ReactNode } from 'react'
import axios from '@fs/zion-axios'
import { useUser } from '@fs/zion-user'
import { i18n } from '@fs/zion-locale'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  MyScheduleProvider as ZionMyScheduleProvider,
  useMyScheduleContext as useZionMyScheduleContext,
} from '@fs/zion-rootstech/dist/es/providers/MyScheduleProvider'
import { useTimeTravel, type MyScheduleContextValue } from '@fs/zion-rootstech'
import { eventConfig } from '../config'
import notifySentry from '../lib/helpers/sentryNotifier'
import { useCalendarDataContext } from './CalendarDataProvider'

interface MyScheduleProviderProps {
  children: ReactNode
}

/**
 * Re-export useMyScheduleContext from zion for app consumption
 */
export function useMyScheduleContext(): MyScheduleContextValue {
  return useZionMyScheduleContext()
}

export default function MyScheduleProvider({ children }: MyScheduleProviderProps): JSX.Element {
  // Get calendar data from context (single source of truth)
  const { calendarData, loading: calendarLoading, error: calendarError } = useCalendarDataContext()

  const user = useUser()
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()

  const userObj = useMemo(
    () => ({
      signedIn: user?.signedIn ?? false,
      cisId: user?.cisId,
      id: user?.id,
      userLoading: user?.userLoading,
    }),
    [user?.signedIn, user?.cisId, user?.id, user?.userLoading]
  )

  const onError = useCallback(
    (error: Error) => {
      const errorMessage = t(
        'generic.api.error.message',
        "We're sorry, but there's an issue loading the content. Please try again shortly."
      )
      showStatusOverlay({
        message: errorMessage,
        type: 'error',
        dismissable: true,
      })
      notifySentry(error)
    },
    [showStatusOverlay, t]
  )

  const locale = i18n.language
  const { conference } = eventConfig
  // Zion takes the clock as a prop so the app can hand it the time-travel offset. Passing Date.now
  // here would leave this provider on the real clock while ScheduleChips, rendered inside it, reads
  // the travelled one. This only works while TimeTravelProvider stays above this provider in the
  // AppProviders list — outside it the hook falls back to the real clock without complaining.
  const { getNow } = useTimeTravel()

  return (
    <ZionMyScheduleProvider
      axiosInstance={axios}
      user={userObj}
      conference={conference}
      // `error` matters as much as `data` here: consolidation hides saved sessions missing from this
      // list and relocates ones found only at another time, and a failed calendar is not evidence of
      // either. Without it, one failed request can silently rewrite the user's saved schedule.
      calendar={{ data: calendarData, loading: calendarLoading, error: calendarError }}
      locale={locale}
      getNow={getNow}
      onError={onError}
    >
      {children}
    </ZionMyScheduleProvider>
  )
}
